<template>
    <v-dialog v-model="dialogOpen" max-width="3000px">
        <v-card>
            <v-card-title>
                {{ $t('Добавление планового срока выполнений мероприятий') }}
            </v-card-title>

            <v-card-text>
                <v-expansion-panels multiple>
                    <v-expansion-panel v-for="(events, group) in groupedEventTypes" :key="group">
                        <v-expansion-panel-header class="group-header">{{ group }}</v-expansion-panel-header>
                        <v-expansion-panel-content>
                            <v-row v-for="event in events" :key="event.event_type_rid" align="center">
                                <v-col cols="10">
                                    <div class="d-flex align-center flex-wrap">
                                        <v-checkbox
                                            :label="event.event_type_name___ru"
                                            :value="event.event_type_rid"
                                            v-model="selectedEvents"
                                            hide-details
                                            dense
                                            @change="onToggleEvent(event.event_type_rid)"
                                            color="gray"
                                            class="white--text mr-4"
                                        />

                                        <div class="flag-line d-flex align-center flex-wrap">
                                            <v-checkbox
                                                v-if="extraFlags[event.event_type_rid] && extraFlags[event.event_type_rid].contract !== null"
                                                dense
                                                hide-details
                                                class="mr-4"
                                                v-model="extraFlags[event.event_type_rid].contract"
                                                :true-value="true"
                                                :false-value="false"
                                                label="Заявка на продление договора"
                                                color="gray"
                                                :disabled="!selectedEvents.includes(event.event_type_rid)"
                                            />

                                            <v-checkbox
                                                v-if="extraFlags[event.event_type_rid] && extraFlags[event.event_type_rid].pute !== null"
                                                dense
                                                hide-details
                                                v-model="extraFlags[event.event_type_rid].pute"
                                                :true-value="true"
                                                :false-value="false"
                                                label="Заявка на проверку ПУТЭ"
                                                color="gray"
                                                :disabled="!selectedEvents.includes(event.event_type_rid)"
                                            />
                                        </div>
                                    </div>
                                </v-col>

                                <v-col cols="2" class="text-right">
                                    <v-menu
                                        v-model="menus[event.event_type_rid]"
                                        :close-on-content-click="false"
                                        transition="scale-transition"
                                        offset-y
                                        min-width="auto"
                                        content-class="period-menu"
                                    >
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-text-field
                                                v-model="formattedDates[event.event_type_rid]"
                                                label="Период"
                                                style="max-width: 150px; width: 100%"
                                                readonly
                                                v-bind="attrs"
                                                v-on="on"
                                                clearable
                                                :disabled="!selectedEvents.includes(event.event_type_rid)"
                                                prepend-icon="mdi-calendar"
                                                @click:clear="resetDatePeriod(event.event_type_rid)"
                                                :error="selectedEvents.includes(event.event_type_rid) && !!getPeriodError(event.event_type_rid)"
                                                :error-messages="
                                                    selectedEvents.includes(event.event_type_rid) && getPeriodError(event.event_type_rid)
                                                        ? [getPeriodError(event.event_type_rid)]
                                                        : []
                                                "
                                            />
                                        </template>

                                        <v-date-picker
                                            v-model="selectedDates[event.event_type_rid]"
                                            @input="val => handleDatePick(event.event_type_rid, val)"
                                            locale="ru-ru"
                                            scrollable
                                            :min="minDate"
                                            :max="maxDate"
                                            :allowed-dates="allowedDate"
                                            color="indigo darken-1"
                                            header-color="indigo darken-1"
                                        />
                                    </v-menu>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-content>
                    </v-expansion-panel>
                </v-expansion-panels>
            </v-card-text>

            <v-card-actions class="justify-end">
                <v-btn text @click="$emit('update:modelValue', false)">
                    {{ $t('Отмена') }}
                </v-btn>
                <v-btn color="gray" class="white--text" @click="save" :disabled="hasMissingPeriods">
                    {{ $t('Добавить') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import axios from 'axios';

export default {
    name: 'CreatePlannedDeadline',
    props: {
        modelValue: { type: Boolean, required: true },
        infoCreateModel: { type: Object, default: () => ({}) }
    },
    emits: ['update:modelValue', 'saved'],
    data() {
        return {
            eventTypes: [],
            groupedEventTypes: {},
            selectedEvents: [],
            selectedDates: {},
            formattedDates: {},
            menus: {},
            originalPlanDates: {},
            extraFlags: {}
        };
    },
    watch: {
        async modelValue(open) {
            if (!open) {
                Object.keys(this.menus).forEach(k => this.$set(this.menus, k, false));
                return;
            }
            this.selectedEvents = [];
            this.selectedDates = {};
            this.formattedDates = {};
            this.menus = {};
            this.originalPlanDates = {};
            await this.fetchEventTypes();
            this.prefillFromModel();
        },
        infoCreateModel: {
            handler() {
                this.prefillFromModel();
            }
            //deep: true
        }
    },
    methods: {
        toBoolOrNull(v) {
            if (v === null || v === undefined) return null;
            if (v === true || v === false) return v;
            const n = Number(v);
            if (Number.isNaN(n)) return null;
            return n === 1;
        },
        allowedDate(ymd) {
            return ymd >= this.minDate && ymd <= this.maxDate;
        },
        isDateOutOfBounds(ymd) {
            const n = this.normalizeDateStr(ymd);
            if (!n) return true;
            return n < this.minDate || n > this.maxDate;
        },
        getPeriodError(rid) {
            const ymd = this.selectedDates[rid] || this.originalPlanDates[rid] || '';
            if (!ymd) return this.$t('Укажите период');
            if (this.isDateOutOfBounds(ymd)) {
                const from = this.formatDate(this.minDate); // dd.mm.yyyy
                const to = this.formatDate(this.maxDate);
                return this.$t(`Дата должна быть в диапазоне ${from} — ${to}`);
            }
            return '';
        },
        hasPlan(rid) {
            return !!(this.selectedDates[rid] || this.originalPlanDates[rid]);
        },

        async fetchEventTypes() {
            try {
                const response = await axios.get('/prescription_ozp/getEventTypes');
                this.eventTypes = response.data.data || [];

                this.groupedEventTypes = this.eventTypes.reduce((groups, item) => {
                    const group = item.event_group_name___ru || 'Прочее';
                    if (!groups[group]) groups[group] = [];
                    groups[group].push(item);
                    return groups;
                }, {});

                for (const event of this.eventTypes) {
                    const rid = event.event_type_rid;
                    this.$set(this.menus, rid, false);
                    this.$set(this.selectedDates, rid, null);
                    this.$set(this.formattedDates, rid, '');

                    this.$set(this.extraFlags, rid, {
                        pute: this.toBoolOrNull(event.pute),
                        contract: this.toBoolOrNull(event.contract)
                    });
                }
            } catch (error) {
                console.error('Ошибка при загрузке типов событий:', error);
                this.$toast?.error?.(this.$t('Не удалось загрузить типы событий'));
            }
        },

        prefillFromModel() {
            const fromPrescription = this.infoCreateModel?.prescription_events || [];
            const fromAhs = this.infoCreateModel?.ahs_events || [];
            const pushOriginal = arr => {
                for (const e of arr || []) {
                    const rid = e?.event_type_rid;
                    if (rid == null) continue;

                    const plan = e?.dead_line_plan;
                    if (plan && this.originalPlanDates[rid] == null) {
                        const normalized = this.normalizeDateStr(plan);
                        this.$set(this.originalPlanDates, rid, normalized);
                    }

                    if (this.extraFlags[rid]) {
                        if (Object.prototype.hasOwnProperty.call(e, 'pute')) {
                            this.$set(this.extraFlags[rid], 'pute', this.toBoolOrNull(e.pute));
                        }
                        if (Object.prototype.hasOwnProperty.call(e, 'contract')) {
                            this.$set(this.extraFlags[rid], 'contract', this.toBoolOrNull(e.contract));
                        }
                    }
                }
            };
            pushOriginal(fromPrescription);
            pushOriginal(fromAhs);

            const base = Array.isArray(fromPrescription) && fromPrescription.length ? fromPrescription : fromAhs;
            if (!Array.isArray(base) || !base.length) return;
            if (!this.eventTypes.length) return;

            for (const e of base) {
                const rid = e?.event_type_rid;
                if (rid == null) continue;

                const toDelete = e?.to_delete === 1;
                if (!toDelete && !this.selectedEvents.includes(rid)) {
                    this.selectedEvents.push(rid);
                }

                const plan = this.originalPlanDates[rid] || e?.dead_line_plan;
                if (plan) {
                    const normalized = this.normalizeDateStr(plan);
                    this.$set(this.selectedDates, rid, normalized);
                    this.$set(this.formattedDates, rid, this.formatDate(normalized));
                }
            }
        },
        onToggleEvent(rid) {
            const selected = this.selectedEvents.includes(rid);
            if (!selected) {
                this.resetDatePeriod(rid);
                const flags = this.extraFlags[rid];
                if (flags) {
                    if (flags.contract !== null) this.$set(this.extraFlags[rid], 'contract', false);
                    if (flags.pute !== null) this.$set(this.extraFlags[rid], 'pute', false);
                }
            }
        },

        handleDatePick(rid, val) {
            const normalized = this.normalizeDateStr(val);
            this.selectedDates[rid] = normalized;
            this.formattedDates[rid] = this.formatDate(normalized);
            this.menus[rid] = false;
        },

        normalizeDateStr(d) {
            if (!d) return '';

            if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;

            if (/^\d{4}-\d{2}-\d{2}T/.test(d)) return d.slice(0, 10);

            const m = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(d);
            if (m) return `${m[3]}-${m[2]}-${m[1]}`;

            const dt = new Date(d);
            if (!isNaN(dt.getTime())) {
                const yyyy = dt.getFullYear();
                const mm = String(dt.getMonth() + 1).padStart(2, '0');
                const dd = String(dt.getDate()).padStart(2, '0');
                return `${yyyy}-${mm}-${dd}`;
            }
            return '';
        },

        formatDate(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            return date.toLocaleDateString('ru-RU', { year: 'numeric', month: 'numeric', day: 'numeric' });
        },

        resetDatePeriod(rid) {
            this.selectedDates[rid] = null;
            this.formattedDates[rid] = '';
        },

        save() {
            if (this.hasMissingPeriods) {
                this.$toast?.error?.(this.$t('Для выбранных мероприятий нужно указать период'));
                return;
            }

            const ahs_events = this.eventTypes.map(event => {
                const rid = event.event_type_rid;
                const isSelected = this.selectedEvents.includes(rid);
                const planCandidate = this.selectedDates[rid] || this.originalPlanDates[rid] || null;
                const flags = this.extraFlags[rid] || { pute: null, contract: null };

                return {
                    event_type_rid: rid,
                    dead_line_plan: planCandidate,
                    to_delete: isSelected ? 0 : 1,

                    pute: isSelected ? flags.pute : null,
                    contract: isSelected ? flags.contract : null
                };
            });

            const processedModel = {
                ...this.infoCreateModel,
                to_delete: 0,
                object_contracts: (this.infoCreateModel.object_contracts || []).map(obj => ({
                    ...obj,
                    to_delete: 0
                })),
                ksk_houses: (this.infoCreateModel.ksk_houses || []).map(obj => ({
                    ...obj,
                    to_delete: 0
                })),
                ahs_events
            };

            axios
                .post('/prescription_ozp/postPrescriptions', { nvcPrescriptionData: processedModel })
                .then(() => {
                    this.$toast?.success?.(this.$t('Предписание успешно сохранено'));
                    this.$emit('update:modelValue', false);
                    this.$emit('saved');
                })
                .catch(err => {
                    console.error('Ошибка при сохранении:', err);
                    this.$toast?.error?.(this.$t('Ошибка при сохранении предписания'));
                });
        }
    },
    computed: {
        dialogOpen: {
            get() {
                return this.modelValue;
            },
            set(v) {
                // пробрасываем в родителя сразу оба варианта
                this.$emit('update:modelValue', v);
                this.$emit('input', v);
            }
        },
        minDate() {
            const d = new Date();
            const yyyy = d.getFullYear();
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const dd = String(d.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        },

        maxDate() {
            const today = new Date();
            const year = today.getFullYear();
            const oct15 = new Date(`${year}-12-15T00:00:00`);
            const max = today > oct15 ? today : oct15;
            const yyyy = max.getFullYear();
            const mm = String(max.getMonth() + 1).padStart(2, '0');
            const dd = String(max.getDate()).padStart(2, '0');
            return `${yyyy}-${mm}-${dd}`;
        },

        hasMissingPeriods() {
            return this.selectedEvents.some(rid => {
                const ymd = this.selectedDates[rid] || this.originalPlanDates[rid] || '';
                return !ymd || this.isDateOutOfBounds(ymd);
            });
        }
    }
};
</script>

<style scoped>
.group-header {
    font-size: 18px;
    font-weight: 400;
}
.period-menu .v-date-picker-header {
    color: #fff !important;
}

.period-menu .v-date-picker-table .v-btn {
    opacity: 1 !important;
    font-weight: 500;
}

.period-menu .v-date-picker-table .v-btn:not(.v-btn--active):hover {
    background: rgba(63, 81, 181, 0.12) !important; /* под indigo */
}

.period-menu .v-date-picker-table .v-btn--active {
    color: #fff !important;
}

.period-menu .v-date-picker-table .v-btn.v-btn--outlined,
.period-menu .v-date-picker-table .v-btn.v-btn--current {
    box-shadow: inset 0 0 0 2px currentColor !important;
}

.period-menu .v-date-picker-table .v-event {
    background-color: #3f51b5 !important; /* indigo */
}
.flag-line {
    flex-basis: 100%;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: -6px;
    padding-left: 40px;
}

.flag-line :deep(.v-input) {
    width: auto !important;
    flex: 0 0 auto !important;
}
</style>
